import React from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { cn } from '@/lib/utils';
import { ChartCard, EmptyChart } from '../charts/ChartPrimitives';
import { formatUnits } from '../../lib/reporting-format';

export type TopSkuRow = {
  skuId: string;
  sku: string;
  category: string;
  units: number;
  value: number;
};

type Measure = 'value' | 'units';

interface TopSkusByValueCardProps {
  loading: boolean;
  rows: TopSkuRow[];
}

/** Top SKUs by stock value, with a units ↔ $ toggle that also re-sorts. */
export const TopSkusByValueCard: React.FC<TopSkusByValueCardProps> = ({ loading, rows }) => {
  const { t } = useTranslation('inventory');
  const { format, currency } = useCurrencyFormatter();
  const [measure, setMeasure] = React.useState<Measure>('value');

  const sorted = React.useMemo(
    () => [...rows].sort((a, b) => b[measure] - a[measure]).slice(0, 20),
    [rows, measure],
  );

  const ToggleButton = ({ value, label }: { value: Measure; label: string }) => (
    <button
      type="button"
      onClick={() => setMeasure(value)}
      aria-pressed={measure === value}
      className={cn(
        'rounded-md px-2.5 py-1 text-xs font-medium transition-colors duration-200 motion-reduce:transition-none',
        measure === value
          ? 'bg-threadheads-purple text-white'
          : 'text-muted-foreground hover:bg-muted',
      )}
    >
      {label}
    </button>
  );

  return (
    <ChartCard
      title={t('reporting.v2.topSkusByValue', 'Top SKUs by value')}
      action={
        <div className="inline-flex items-center rounded-md border bg-muted/40 p-0.5">
          <ToggleButton value="value" label={t('reporting.v2.measureValue', '$ Value')} />
          <ToggleButton value="units" label={t('reporting.v2.measureUnits', 'Units')} />
        </div>
      }
    >
      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-9 animate-pulse rounded bg-muted/40" />
          ))}
        </div>
      ) : sorted.length === 0 ? (
        <EmptyChart message={t('reporting.v2.noTopSkus', 'No valued stock to rank.')} />
      ) : (
        <div className="max-h-96 overflow-auto rounded-md border">
          <table className="w-full min-w-[26rem] border-collapse text-sm">
            <thead className="sticky top-0 z-10 bg-card">
              <tr className="border-b text-left text-xs font-medium text-muted-foreground">
                <th className="py-2 pl-3 pr-3 font-medium">{t('reporting.v2.sku', 'SKU')}</th>
                <th className="py-2 px-3 text-right font-medium">
                  {t('reporting.v2.units', 'Units')}
                </th>
                <th className="py-2 pl-3 pr-3 text-right font-medium">
                  {t('reporting.v2.value', 'Value')}
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((row) => (
                <tr key={row.skuId || row.sku} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pl-3 pr-3">
                    <p className="font-mono text-xs font-medium">{row.sku}</p>
                    <p className="text-xs text-muted-foreground">{row.category}</p>
                  </td>
                  <td
                    className={cn(
                      'py-2 px-3 text-right tabular-nums',
                      measure === 'units' ? 'font-semibold' : 'text-muted-foreground',
                    )}
                  >
                    {formatUnits(row.units)}
                  </td>
                  <td
                    className={cn(
                      'py-2 pl-3 pr-3 text-right tabular-nums',
                      measure === 'value' ? 'font-semibold' : 'text-muted-foreground',
                    )}
                  >
                    {format(row.value, currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ChartCard>
  );
};
